import { useCallback, useMemo } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { useLabels } from "~/hooks/useLabels";
import { SidePanelLayout } from "~/components/sidePanelLayout/SidePanelLayout";
import { DownloadsCard } from "~/components/downloadsCard/DownloadsCard";
import { track } from "~/utils/track";
import { ClassroomDownloadType } from "~/graphql/types";

import type { IconVariantProp } from "~/components/icon/Icon";
import type { SessionRouteLoaderData } from "./route";

type Download = {
  [k: string]: {
    title: string;
    description: string;
    iconName: string;
    iconVariant: IconVariantProp;
  };
};

export interface SessionDownloadsProps {
  onRequestClose?: () => void;
}

export function SessionDownloads({ onRequestClose }: SessionDownloadsProps) {
  const { t } = useLabels();

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  const trackingData = useMemo(
    () => ({
      className: data.session.class?.slug,
      sessionNumber: data.session.position,
      trigger: "sessionNotebook",
    }),
    [data.session.class?.slug, data.session.position],
  );

  const handleClick = useCallback(
    (url: string) => {
      if (url.includes("teacher-notes")) {
        track("crDownloadTeacherNotes", trackingData);
      }
      if (url.includes("instructor-translation")) {
        track("crDownloadLiteralLiteraryTranslation", trackingData);
      }
      globalThis.open(url);
    },
    [trackingData],
  );

  const downloadMap: Download = useMemo(
    () => ({
      [ClassroomDownloadType.TeacherNotes]: {
        title: t("downloads.teacherNotes.title"),
        description: t("downloads.teacherNotes.description"),
        iconName: "chalkboard-user",
        iconVariant: "regular",
      },
      [ClassroomDownloadType.LiteralLiteraryTranslation]: {
        title: t("downloads.literalLiteraryTranslation.title"),
        description: t("downloads.literalLiteraryTranslation.description"),
        iconName: "memo-pad",
        iconVariant: "regular",
      },
    }),
    [t],
  );

  return (
    <SidePanelLayout
      title={t("session.tab.downloads")}
      onRequestClose={onRequestClose}
    >
      <ol className="SessionDownloads">
        {data.session.class?.downloads?.map((download) => {
          if (!downloadMap[download.type]) {
            return null;
          }
          return (
            <li key={download.type}>
              <DownloadsCard
                title={downloadMap[download.type].title}
                description={downloadMap[download.type].description}
                artworkColor={data.session.class?.color.primary}
                icon={downloadMap[download.type].iconName}
                iconVariant={downloadMap[download.type].iconVariant}
                onClick={() => handleClick(download.url)}
              />
            </li>
          );
        })}
      </ol>
    </SidePanelLayout>
  );
}
